import React, { useState } from 'react';
import { X, Save, Loader2 } from 'lucide-react';
import { savedAnalysesService } from '../../services/savedAnalyses';
import { CalculatorParams, AnalysisResponse } from '../../types/api';
import { Scenario } from '../../types/app';

interface SaveAnalysisModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
  scenarios: Scenario[];
  timeHorizonYears: number;
  initialTitle?: string;
}

export const SaveAnalysisModal: React.FC<SaveAnalysisModalProps> = ({
  isOpen,
  onClose,
  onSaved,
  scenarios,
  timeHorizonYears,
  initialTitle
}) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Autofill title when modal opens
  React.useEffect(() => {
    if (isOpen) {
      setTitle(initialTitle || '');
    }
  }, [isOpen, initialTitle]);

  const handleSave = async () => {
    if (!title.trim()) {
      setError('Title is required');
      return;
    }

    try {
      setIsSaving(true);
      setError(null);
      
      // Convert scenarios to saved format
      const savedScenarios = scenarios.map(scenario => ({
        title: scenario.title,
        scenarioType: scenario.scenarioType,
        parameters: scenario.parameters,
        isBaseline: scenario.isBaseline,
        link: scenario.link
      }));
      // Overwrite by title: if an analysis with the same title exists, delete it first
      try {
        const existing = (await savedAnalysesService.getUserAnalyses())
          .find(a => a.title.trim().toLowerCase() === title.trim().toLowerCase());
        if (existing?.id) {
          await savedAnalysesService.deleteAnalysis(existing.id);
        }
      } catch (e) {
        console.warn('Failed to check/delete existing analysis before save:', e);
        // Proceed to save anyway
      }

      await savedAnalysesService.saveAnalysisWithScenarios(
        title.trim(),
        description.trim(),
        savedScenarios,
        timeHorizonYears
      );
      
      onSaved();
      onClose();
      
      // Reset form
      setTitle('');
      setDescription('');
    } catch (error) {
      console.error('Failed to save analysis:', error);
      setError('Failed to save analysis. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    if (!isSaving) {
      onClose();
      setTitle('');
      setDescription('');
      setError(null);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Save Analysis</h2>
          <button
            onClick={handleClose}
            disabled={isSaving}
            className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
        
        <div className="p-6">
          <div className="space-y-4">
            <div>
              <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
                Title *
              </label>
              <input
                id="title"
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g., First Home Purchase Analysis"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                disabled={isSaving}
              />
            </div>

            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Optional description of this analysis..."
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                disabled={isSaving}
              />
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}
          </div>

          <div className="flex gap-3 mt-6">
            <button
              onClick={handleSave}
              disabled={isSaving || !title.trim()}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Save className="w-5 h-5" />
              )}
              {isSaving ? 'Saving...' : 'Save Analysis'}
            </button>
            
            <button
              onClick={handleClose}
              disabled={isSaving}
              className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
